<!-- src/components/trends/TrendChart.vue -->
<!-- 회원권 추세 화면의 인라인 SVG 차트(선·막대·쌓은 막대). 새 의존성 없음 -->
<!-- (docs/architecture/admin-trends-design.md 4.8절). 숫자는 늘 옆 표에 있고 차트는 거든다 -->
<template>
  <div class="trend-chart">
    <!-- 계열이 둘 이상이면 범례가 늘 있다(색만으로 구분하지 않게). 선은 짧은 선, 막대는 네모 -->
    <ul v-if="series.length > 1" class="legend">
      <li v-for="(s, i) in series" :key="s.key || i" class="legend-item">
        <svg
          class="legend-key"
          width="14"
          height="10"
          viewBox="0 0 14 10"
          aria-hidden="true"
        >
          <line
            v-if="kind === 'line'"
            x1="0"
            y1="5"
            x2="14"
            y2="5"
            :stroke="colorOf(i)"
            stroke-width="2"
            stroke-linecap="round"
          />
          <rect
            v-else
            x="2"
            y="0"
            width="10"
            height="10"
            rx="2"
            :fill="colorOf(i)"
          />
        </svg>
        <span>{{ s.label }}</span>
      </li>
    </ul>

    <!-- 키보드: 초점을 두고 ←/→로 달을 옮기면 툴팁에 그 달 값이 나온다(마우스와 같은 내용) -->
    <div
      ref="box"
      class="chart-box"
      tabindex="0"
      role="group"
      :aria-label="`${title}. 왼쪽·오른쪽 화살표로 달마다 값을 봅니다.`"
      @keydown="onKeydown"
      @focus="onFocus"
      @blur="hover = -1"
    >
      <svg
        class="chart-svg"
        role="img"
        :aria-label="fullAriaLabel"
        :width="width"
        :height="height"
        :viewBox="`0 0 ${width} ${height}`"
        @pointerleave="onPointerLeave"
      >
        <!-- <title>은 두지 않는다: 브라우저가 차트 위 어디서나 긴 기본 툴팁으로 띄워 아래 툴팁을 가린다(요약은 aria-label) -->

        <!-- 가로 격자(흐린 가는 선)와 y축 값 -->
        <g class="grid">
          <g v-for="t in ticks" :key="`t${t}`">
            <line
              :x1="margin.left"
              :x2="width - margin.right"
              :y1="yOf(t)"
              :y2="yOf(t)"
              :stroke="t === 0 ? AXIS_COLOR : GRID_COLOR"
              stroke-width="1"
              shape-rendering="crispEdges"
            />
            <text
              class="tick"
              :x="margin.left - 6"
              :y="yOf(t)"
              text-anchor="end"
              dominant-baseline="middle"
            >
              {{ tickText(t) }}
            </text>
          </g>
        </g>

        <!-- 가리킨 달: 선은 세로 십자선, 막대는 그 달 띠를 살짝 밝힌다 -->
        <template v-if="hover >= 0">
          <line
            v-if="kind === 'line'"
            class="crosshair"
            :x1="xOf(hover)"
            :x2="xOf(hover)"
            :y1="margin.top"
            :y2="margin.top + plotH"
            stroke="#9ca3af"
            stroke-opacity="0.6"
            stroke-width="1"
            shape-rendering="crispEdges"
          />
          <rect
            v-else
            :x="margin.left + band * hover"
            :y="margin.top"
            :width="band"
            :height="plotH"
            fill="#ffffff"
            fill-opacity="0.05"
          />
        </template>

        <!-- 막대 / 쌓은 막대: 바닥에 붙이고 끝 4px 둥글게, 쌓은 칸 사이 2px 바탕색 틈 -->
        <g v-if="kind !== 'line'">
          <g
            v-for="bar in bars"
            :key="`b${bar.index}`"
            :opacity="bar.partial ? 0.5 : 1"
          >
            <path
              v-for="seg in bar.segments"
              :key="seg.key"
              :d="seg.d"
              :fill="seg.color"
            />
          </g>
        </g>

        <!-- 선: 2px, 값이 null(기록 전·분모 0)이면 끊고, 진행 중인 마지막 구간은 점선 -->
        <g v-else>
          <g v-for="line in lines" :key="`l${line.index}`">
            <path
              v-for="(d, k) in line.solid"
              :key="`s${k}`"
              :d="d"
              fill="none"
              :stroke="line.color"
              stroke-width="2"
              stroke-linejoin="round"
              stroke-linecap="round"
            />
            <path
              v-if="line.dashed"
              :d="line.dashed"
              fill="none"
              :stroke="line.color"
              stroke-width="2"
              stroke-dasharray="4 4"
              stroke-linecap="round"
            />
            <!-- 점 8px, 바탕색 2px 고리. 진행 중·집계 중·표본 적은 점은 속 빈 원. 달 사이가 좁으면(DENSE_BAND) 작게, 일부만 -->
            <circle
              v-for="p in line.points"
              :key="`p${p.index}`"
              :cx="p.x"
              :cy="p.y"
              :r="p.r"
              :fill="p.hollow ? SURFACE : line.color"
              :stroke="p.hollow ? line.color : SURFACE"
              :stroke-width="p.ring"
            />
          </g>
        </g>

        <!-- 끝 글자: 한 계열이면 마지막 값, 여러 계열이면 이름(겹치면 쓰지 않고 범례·툴팁에 맡긴다) -->
        <text
          v-for="label in endLabels"
          :key="`e${label.key}`"
          class="end-label"
          :x="label.x"
          :y="label.y"
          :text-anchor="label.anchor"
          dominant-baseline="middle"
        >
          {{ label.text }}
        </text>

        <!-- x축 달 글자: 좁으면 몇 달에 하나(마지막 달은 늘 보임) -->
        <text
          v-for="i in xLabelIndexes"
          :key="`x${i}`"
          class="tick"
          :x="xOf(i)"
          :y="height - 6"
          text-anchor="middle"
        >
          {{ monthLabel(labels[i]) }}
        </text>

        <!-- 누름 영역: 차트 전체. 가리킨 x에서 가장 가까운 달로 붙는다(표시보다 크게, 끝 글자 여백 포함) -->
        <rect
          class="hit"
          x="0"
          y="0"
          :width="width"
          :height="height"
          fill="transparent"
          @pointermove="onPointer"
          @pointerdown="onPointer"
        />
      </svg>

      <!-- 툴팁: 그 달 모든 계열 값(값이 앞, 이름이 뒤). 화면 읽기 프로그램에는 바뀔 때 읽힌다 -->
      <div
        v-show="tooltip"
        class="tooltip"
        :class="{ 'tooltip--left': tooltip && tooltip.flip }"
        :style="tooltip ? { left: `${tooltip.x}px` } : null"
        aria-live="polite"
      >
        <template v-if="tooltip">
          <div class="tt-month">
            {{ tooltip.month }}
            <span v-if="tooltip.partial" class="tt-note">진행 중</span>
          </div>
          <div v-for="row in tooltip.rows" :key="row.key" class="tt-row">
            <svg
              class="tt-key"
              width="12"
              height="4"
              viewBox="0 0 12 4"
              aria-hidden="true"
            >
              <line
                x1="1"
                y1="2"
                x2="11"
                y2="2"
                :stroke="row.color"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
            <strong class="tt-value">{{ row.value }}</strong>
            <span class="tt-label">{{ row.label }}</span>
            <span v-if="row.note" class="tt-note">{{ row.note }}</span>
          </div>
          <div v-if="tooltip.total" class="tt-row tt-total">
            <strong class="tt-value">{{ tooltip.total }}</strong>
            <span class="tt-label">합계</span>
          </div>
        </template>
      </div>
    </div>

    <p v-if="captions.length" class="caption">{{ captions.join(" · ") }}</p>
  </div>
</template>

<script>
import {
  SERIES_COLORS,
  formatUnit,
  monthLabel,
  monthLabelLong,
} from "@/lib/trends";

// 다크 카드 바탕(#151718)과 같은 색: 점의 고리·속 빈 원·쌓은 막대 틈
const SURFACE = "#151718";
const GRID_COLOR = "#2a2d2e";
const AXIS_COLOR = "#3a3d3e";
const BAR_MAX_WIDTH = 24;
const BAR_RADIUS = 4;
const STACK_GAP = 2;
// x축 달 글자("26.10") 하나가 차지하는 너비. 띠가 이보다 좁으면 몇 달에 하나만 쓴다
const X_LABEL_SPACE = 38;
// 끝 글자끼리 이보다 가까우면 겹친 것으로 보고 쓰지 않는다
const END_LABEL_MIN_GAP = 14;
// 달 사이(점 사이)가 이보다 좁으면 점을 모두 찍지 않는다: 8px 점과 2px 고리(10px)가 이웃 점 사이 선을 다 덮어, 24개월 차트를
// 1280~1440px에서 세 개 한 줄로 그리면 선과 진행 중 점선이 사라지고 흩어진 점만 남았다(리뷰 3차). 그때는 선을 그리고 끝 점·속 빈
// 점·이웃이 없어 선이 그려지지 않는 점만 작게(지름 6px, 고리 1.5px) 찍는다
const DENSE_BAND = 14;
const CHAR_PX = 7;
// 위·아래 여백(아래는 x축 달 글자 자리). y 위치는 이 값만 보고 오른쪽 여백(끝 글자)과 따로 계산한다
const PAD_TOP = 14;
const PAD_BOTTOM = 24;

function isNum(x) {
  return typeof x === "number" && Number.isFinite(x);
}

// 축 눈금 간격: 1·2·2.5·5 × 10^n
function niceStep(raw) {
  if (!(raw > 0)) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(raw)));
  const f = raw / p;
  const m = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
  return m * p;
}

// 위가 둥근(4px) 막대 path. 바닥 쪽은 각지게 둔다
function roundedTopBar(x, y, w, h, r) {
  const rr = Math.max(0, Math.min(r, w / 2, h));
  return [
    `M${x},${y + h}`,
    `L${x},${y + rr}`,
    `Q${x},${y} ${x + rr},${y}`,
    `L${x + w - rr},${y}`,
    `Q${x + w},${y} ${x + w},${y + rr}`,
    `L${x + w},${y + h}`,
    "Z",
  ].join(" ");
}

function plainBar(x, y, w, h) {
  return `M${x},${y + h} L${x},${y} L${x + w},${y} L${x + w},${y + h} Z`;
}

export default {
  name: "TrendChart",
  props: {
    // line(선)·bar(막대)·stacked-bar(쌓은 막대)
    kind: { type: String, default: "line" },
    // 월 "YYYY-MM" 배열 (오래된 달 → 이번 달)
    labels: { type: Array, default: () => [] },
    // [{ key, label, values: (number|null)[], hollow?: boolean[], notes?: string[] }], 색은 순서대로(SERIES_COLORS).
    // hollow 점은 속 빈 원이고 끝 글자·aria-label 요약에 쓰지 않으며, 툴팁에는 notes[i](까닭, 예 "집계 중")를 붙인다
    series: { type: Array, default: () => [] },
    // count·pct·won·manwon (툴팁·축 값 형식)
    unit: { type: String, default: "count" },
    // 마지막 달이 진행 중(속 빈 점·점선, 막대는 흐리게)
    partialLast: { type: Boolean, default: false },
    // 차트 이름. 화면 읽기 프로그램에는 처음·마지막 달 값 요약을 붙여 읽힌다
    ariaLabel: { type: String, default: "" },
    height: { type: Number, default: 220 },
  },
  data() {
    return {
      SURFACE,
      GRID_COLOR,
      AXIS_COLOR,
      width: 600,
      hover: -1,
    };
  },
  computed: {
    n() {
      return this.labels.length;
    },
    title() {
      return this.ariaLabel || "차트";
    },
    // "활성 회원: 2025년 11월 150명 → 2026년 10월 182명"처럼 계열마다 처음·마지막 값(한 계열이면 이름은 제목이 대신한다).
    // 속 빈 점(hollow: 집계 중·표본 적음)의 값은 쓰지 않는다(끝 글자와 같은 규칙. 아직 바뀌는 이번 달 이탈률이 끝 값으로 읽히면
    // 바로 위 타일의 확정 값과 어긋나 "이탈이 뛰었다"로 읽힌다). 뺀 값이 끝에 있으면 그렇다고 덧붙인다
    fullAriaLabel() {
      const single = this.series.length === 1;
      let skippedEnd = false;
      const parts = this.series
        .map((s) => {
          const vals = Array.isArray(s.values) ? s.values : [];
          const hollow = Array.isArray(s.hollow) ? s.hollow : [];
          let first = -1;
          let last = -1;
          let lastAny = -1;
          vals.forEach((v, i) => {
            if (!isNum(v)) return;
            lastAny = i;
            if (hollow[i]) return;
            if (first < 0) first = i;
            last = i;
          });
          if (last >= 0 && lastAny > last) skippedEnd = true;
          const name = single ? "" : `${s.label} `;
          if (first < 0) {
            return lastAny >= 0
              ? `${name}집계 중이거나 표본이 적은 값만 있음`
              : `${name}값 없음`;
          }
          const a = `${monthLabelLong(this.labels[first])} ${this.fmt(
            vals[first]
          )}`;
          if (first === last) return `${name}${a}`;
          const b = `${monthLabelLong(this.labels[last])} ${this.fmt(
            vals[last]
          )}`;
          return `${name}${a} → ${b}`;
        })
        .filter(Boolean);
      const tail = skippedEnd
        ? " (그 뒤 달은 집계 중이거나 표본이 적어 뺐습니다)"
        : this.partialLast && this.lastDrawn
        ? " (마지막 달은 진행 중)"
        : "";
      return `${this.title}: ${parts.join(", ")}${tail}`;
    },
    // 축 최댓값: 선·막대는 값의 최댓값, 쌓은 막대는 달마다 합의 최댓값
    maxValue() {
      let max = 0;
      if (this.kind === "stacked-bar") {
        for (let i = 0; i < this.n; i++) {
          let sum = 0;
          for (const s of this.series) {
            const v = s.values && s.values[i];
            if (isNum(v) && v > 0) sum += v;
          }
          max = Math.max(max, sum);
        }
      } else {
        for (const s of this.series) {
          for (const v of s.values || []) {
            if (isNum(v)) max = Math.max(max, v);
          }
        }
      }
      return max;
    },
    step() {
      const raw = this.maxValue > 0 ? this.maxValue / 4 : 0;
      let step = niceStep(raw);
      if (this.unit === "pct" && !(this.maxValue > 0)) step = 0.25;
      // 사람 수·금액은 정수 눈금
      if (this.unit !== "pct") step = Math.max(1, Math.ceil(step));
      return step;
    },
    yTop() {
      const top = Math.ceil(this.maxValue / this.step) * this.step;
      return top > 0 ? top : this.step * 4;
    },
    ticks() {
      const out = [];
      const count = Math.round(this.yTop / this.step);
      for (let k = 0; k <= count; k++) out.push(+(k * this.step).toFixed(6));
      return out;
    },
    yLabelWidth() {
      const longest = this.ticks.reduce(
        (m, t) => Math.max(m, this.tickText(t).length),
        1
      );
      return longest * CHAR_PX + 10;
    },
    plotH() {
      return Math.max(40, this.height - PAD_TOP - PAD_BOTTOM);
    },
    // 끝 글자를 쓸지와 그 너비(오른쪽 여백). y만으로 겹침을 판단하므로 너비 계산 전에 정한다.
    // 한 계열이면 끝 점의 값을 쓰는데, 그 점이 속 빈 원으로 표시된 값(hollow: 집계 중·표본 적음)이면 쓰지 않는다:
    // 차트에 하나뿐인 숫자가 아직 바뀌는 값이면 "이탈률이 뛰었다"로 읽힌다(확정 값은 타일과 표에 있다)
    endLabelPlan() {
      if (this.kind !== "line" || !this.series.length || this.series.length > 4)
        return [];
      const single = this.series.length === 1;
      const plan = [];
      this.series.forEach((s, si) => {
        const vals = Array.isArray(s.values) ? s.values : [];
        const hollow = Array.isArray(s.hollow) ? s.hollow : [];
        for (let i = vals.length - 1; i >= 0; i--) {
          if (isNum(vals[i])) {
            if (single && hollow[i]) break;
            const text = single ? this.fmt(vals[i]) : String(s.label);
            plan.push({ key: s.key || si, index: i, value: vals[i], text });
            break;
          }
        }
      });
      // 끝 점이 다른 계열과 너무 가까우면 모두 쓰지 않는다(위아래로 밀면 선과 떨어져 오히려 헷갈린다)
      const ys = plan.map((p) => this.yOf(p.value)).sort((a, b) => a - b);
      for (let k = 1; k < ys.length; k++) {
        if (ys[k] - ys[k - 1] < END_LABEL_MIN_GAP) return [];
      }
      return plan;
    },
    margin() {
      const endWidth = this.endLabelPlan.reduce(
        (m, p) => Math.max(m, p.text.length * CHAR_PX + 12),
        0
      );
      return {
        top: PAD_TOP,
        right: Math.max(12, Math.min(endWidth, 96)),
        bottom: PAD_BOTTOM,
        left: this.yLabelWidth,
      };
    },
    plotW() {
      return Math.max(40, this.width - this.margin.left - this.margin.right);
    },
    band() {
      return this.n > 0 ? this.plotW / this.n : this.plotW;
    },
    xLabelIndexes() {
      const step = Math.max(1, Math.ceil(X_LABEL_SPACE / this.band));
      const out = [];
      for (let i = 0; i < this.n; i++) {
        if ((this.n - 1 - i) % step === 0) out.push(i);
      }
      return out;
    },
    lines() {
      if (this.kind !== "line") return [];
      const lastIdx = this.n - 1;
      return this.series.map((s, si) => {
        const vals = Array.isArray(s.values) ? s.values : [];
        const hollow = Array.isArray(s.hollow) ? s.hollow : [];
        const solid = [];
        let current = "";
        for (let i = 0; i < this.n; i++) {
          const v = vals[i];
          const partialHere = this.partialLast && i === lastIdx;
          if (!isNum(v) || partialHere) {
            if (current) solid.push(current);
            current = "";
            continue;
          }
          const cmd = `${this.xOf(i).toFixed(1)},${this.yOf(v).toFixed(1)}`;
          current = current ? `${current} L${cmd}` : `M${cmd}`;
        }
        if (current) solid.push(current);
        let dashed = "";
        if (
          this.partialLast &&
          lastIdx >= 1 &&
          isNum(vals[lastIdx]) &&
          isNum(vals[lastIdx - 1])
        ) {
          dashed = `M${this.xOf(lastIdx - 1).toFixed(1)},${this.yOf(
            vals[lastIdx - 1]
          ).toFixed(1)} L${this.xOf(lastIdx).toFixed(1)},${this.yOf(
            vals[lastIdx]
          ).toFixed(1)}`;
        }
        const dense = this.band < DENSE_BAND;
        let lastNum = -1;
        for (let i = 0; i < this.n; i++) if (isNum(vals[i])) lastNum = i;
        const points = [];
        for (let i = 0; i < this.n; i++) {
          if (!isNum(vals[i])) continue;
          const isHollow =
            !!hollow[i] || (this.partialLast && i === lastIdx) || false;
          const isolated = !isNum(vals[i - 1]) && !isNum(vals[i + 1]);
          if (dense && !isHollow && i !== lastNum && !isolated) continue;
          points.push({
            index: i,
            x: this.xOf(i),
            y: this.yOf(vals[i]),
            hollow: isHollow,
            r: dense ? 3 : 4,
            ring: dense ? 1.5 : 2,
          });
        }
        return {
          index: si,
          color: this.colorOf(si),
          solid,
          dashed,
          points,
        };
      });
    },
    bars() {
      if (this.kind === "line") return [];
      const out = [];
      const stacked = this.kind === "stacked-bar";
      const groups = stacked ? 1 : Math.max(1, this.series.length);
      const totalW = Math.min(
        stacked ? BAR_MAX_WIDTH : BAR_MAX_WIDTH * groups,
        this.band * 0.7
      );
      const w = stacked
        ? totalW
        : Math.max(2, (totalW - STACK_GAP * (groups - 1)) / groups);
      for (let i = 0; i < this.n; i++) {
        const segments = [];
        const left = this.xOf(i) - totalW / 2;
        if (stacked) {
          let cum = 0;
          const parts = [];
          this.series.forEach((s, si) => {
            const v = s.values && s.values[i];
            if (isNum(v) && v > 0) parts.push({ si, v });
          });
          parts.forEach((p, k) => {
            const yTop = this.yOf(cum + p.v);
            const yBottom = this.yOf(cum);
            cum += p.v;
            // 아래 칸과 사이에 2px 틈(맨 아래 칸은 바닥에 붙인다)
            const bottom = k === 0 ? yBottom : yBottom - STACK_GAP;
            const h = bottom - yTop;
            if (h <= 0.5) return;
            const top = k === parts.length - 1;
            segments.push({
              key: `${i}-${p.si}`,
              color: this.colorOf(p.si),
              d: top
                ? roundedTopBar(left, yTop, w, h, BAR_RADIUS)
                : plainBar(left, yTop, w, h),
            });
          });
        } else {
          this.series.forEach((s, si) => {
            const v = s.values && s.values[i];
            if (!isNum(v) || v <= 0) return;
            const yTop = this.yOf(v);
            const h = this.yOf(0) - yTop;
            if (h <= 0.5) return;
            segments.push({
              key: `${i}-${si}`,
              color: this.colorOf(si),
              d: roundedTopBar(
                left + si * (w + STACK_GAP),
                yTop,
                w,
                h,
                BAR_RADIUS
              ),
            });
          });
        }
        out.push({
          index: i,
          partial: this.partialLast && i === this.n - 1,
          segments,
        });
      }
      return out;
    },
    endLabels() {
      if (this.kind === "line") {
        return this.endLabelPlan.map((p) => ({
          key: p.key,
          x: this.xOf(p.index) + 8,
          y: this.yOf(p.value),
          anchor: "start",
          text: p.text,
        }));
      }
      // 막대에는 끝 글자를 쓰지 않는다: 마지막(진행 중) 막대가 낮으면 글자가 옆 막대 위에 겹쳤다. 값은 툴팁과 표에 있다
      return [];
    },
    tooltip() {
      const i = this.hover;
      if (i < 0 || i >= this.n) return null;
      // 속 빈 점(집계 중·표본 적음)의 값에는 까닭을 붙인다(계열의 notes[i], 없으면 "아직 바뀌는 값")
      const rows = this.series.map((s, si) => {
        const v = s.values ? s.values[i] : null;
        const hollow = isNum(v) && Array.isArray(s.hollow) && !!s.hollow[i];
        const note = Array.isArray(s.notes) ? s.notes[i] : "";
        return {
          key: s.key || si,
          label: s.label,
          color: this.colorOf(si),
          value: this.fmt(v),
          note: hollow ? note || "아직 바뀌는 값" : "",
        };
      });
      let total = "";
      if (this.kind === "stacked-bar" && this.series.length > 1) {
        let sum = 0;
        let any = false;
        for (const s of this.series) {
          const v = s.values && s.values[i];
          if (isNum(v)) {
            sum += v;
            any = true;
          }
        }
        total = any ? this.fmt(sum) : "";
      }
      const x = this.xOf(i);
      return {
        month: monthLabelLong(this.labels[i]),
        partial: this.partialLast && i === this.n - 1,
        rows,
        total,
        x: x > this.width / 2 ? x - 12 : x + 12,
        flip: x > this.width / 2,
      };
    },
    // 진행 중인 마지막 달에 실제로 그린 표시가 있는지(선은 값이 있는 점, 막대는 0보다 큰 막대). 이번 달 값이 아직 없으면(매달 1~8일의
    // 이탈률은 판정이 하나도 없다) 선은 지난달의 속 빈 점에서 끝나므로 "진행 중인 이번 달" 설명을 붙이지 않는다(리뷰 6차)
    lastDrawn() {
      if (!this.partialLast || this.n < 1) return false;
      const last = this.n - 1;
      return this.series.some((s) => {
        const v = s.values && s.values[last];
        return this.kind === "line" ? isNum(v) : isNum(v) && v > 0;
      });
    },
    captions() {
      const out = [];
      if (this.partialLast && this.lastDrawn) {
        if (this.kind !== "line") out.push("흐린 막대는 진행 중인 이번 달");
        else if (this.lines.some((l) => l.dashed))
          out.push("점선 끝 속 빈 점은 진행 중인 이번 달");
        else out.push("맨 끝 속 빈 점은 진행 중인 이번 달");
      }
      // 그리는 점(값이 있는 달)만 센다: 값이 없는 달의 hollow로 보이지 않는 점을 설명하지 않게(리뷰 3차)
      if (this.kind === "line") {
        const last = this.n - 1;
        const otherHollow = this.series.some(
          (s) =>
            Array.isArray(s.hollow) &&
            s.hollow.some(
              (h, i) =>
                h &&
                isNum(s.values && s.values[i]) &&
                !(this.partialLast && i === last)
            )
        );
        if (otherHollow) out.push("속 빈 점은 아직 바뀌거나 표본이 적은 값");
      }
      return out;
    },
  },
  mounted() {
    this.measure();
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.$refs.box);
    } else {
      window.addEventListener("resize", this.measure);
    }
  },
  beforeUnmount() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    window.removeEventListener("resize", this.measure);
  },
  methods: {
    monthLabel,
    // 부모 너비에 맞춰 viewBox를 다시 계산한다(글자 크기가 늘 같게)
    measure() {
      const box = this.$refs.box;
      if (!box) return;
      const w = Math.round(box.clientWidth);
      if (w > 0 && Math.abs(w - this.width) >= 1) this.width = Math.max(200, w);
    },
    colorOf(i) {
      return SERIES_COLORS[i] || SERIES_COLORS[SERIES_COLORS.length - 1];
    },
    fmt(v) {
      return formatUnit(isNum(v) ? v : null, this.unit);
    },
    xOf(i) {
      return this.margin.left + this.band * (i + 0.5);
    },
    yOf(v) {
      const top = this.yTop || 1;
      const ratio = Math.max(0, Math.min(1.05, (isNum(v) ? v : 0) / top));
      return PAD_TOP + this.plotH * (1 - ratio);
    },
    // 축 값은 짧게: 20%, 1,200만, 1,234
    tickText(v) {
      if (this.unit === "pct") {
        const p = Math.round(v * 1000) / 10;
        return Number.isInteger(p) ? `${p}%` : `${p.toFixed(1)}%`;
      }
      if (this.unit === "won" || this.unit === "manwon") {
        if (Math.abs(v) >= 10000) {
          return `${(v / 10000).toLocaleString("ko-KR", {
            maximumFractionDigits: 1,
          })}만`;
        }
        return v.toLocaleString("ko-KR");
      }
      return v.toLocaleString("ko-KR");
    },
    // 가리킨 x → 가장 가까운 달 (마우스·터치·펜)
    onPointer(event) {
      if (!this.n) return;
      const svg = event.currentTarget && event.currentTarget.ownerSVGElement;
      const rect = (svg || this.$refs.box).getBoundingClientRect();
      const scale = rect.width > 0 ? this.width / rect.width : 1;
      const x = (event.clientX - rect.left) * scale;
      const i = Math.floor((x - this.margin.left) / this.band);
      this.hover = Math.max(0, Math.min(this.n - 1, i));
    },
    // 터치는 손을 떼면 pointerleave가 바로 오므로 툴팁을 남겨 둔다(다른 곳을 누르면 그 달로 바뀐다)
    onPointerLeave(event) {
      if (event && event.pointerType === "touch") return;
      this.hover = -1;
    },
    onFocus() {
      if (this.hover < 0 && this.n > 0) this.hover = this.n - 1;
    },
    onKeydown(event) {
      if (!this.n) return;
      if (event.key === "ArrowLeft") {
        this.hover = this.hover <= 0 ? 0 : this.hover - 1;
      } else if (event.key === "ArrowRight") {
        this.hover =
          this.hover < 0 ? this.n - 1 : Math.min(this.n - 1, this.hover + 1);
      } else if (event.key === "Home") {
        this.hover = 0;
      } else if (event.key === "End") {
        this.hover = this.n - 1;
      } else if (event.key === "Escape") {
        this.hover = -1;
        return;
      } else {
        return;
      }
      event.preventDefault();
    },
  },
};
</script>

<style scoped>
.trend-chart {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 12px;
  color: #c8c8c8;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.legend-key {
  flex-shrink: 0;
}

.chart-box {
  position: relative;
  width: 100%;
  min-width: 0;
  border-radius: 6px;
  outline: none;
}

.chart-box:focus-visible {
  box-shadow: 0 0 0 2px #78ff47;
}

.chart-svg {
  display: block;
  width: 100%;
  overflow: visible;
}

.tick {
  font-size: 11px;
  fill: #9ca3af;
  font-variant-numeric: tabular-nums;
}

.end-label {
  font-size: 11px;
  fill: #e5e5e5;
  font-variant-numeric: tabular-nums;
}

.tooltip {
  position: absolute;
  top: 6px;
  z-index: 2;
  min-width: 120px;
  max-width: 220px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #333;
  background: #0a0a0a;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.45);
  font-size: 12px;
  color: #e5e5e5;
  pointer-events: none;
}

.tooltip--left {
  transform: translateX(-100%);
}

/* "2026년 10월 진행 중"을 한 줄로: 마지막 달 툴팁은 왼쪽으로 뒤집혀 최소 너비로 줄어 "진행 / 중"으로 끊겼다(리뷰 3차) */
.tt-month {
  margin-bottom: 4px;
  color: #9ca3af;
  white-space: nowrap;
}

.tt-note {
  margin-left: 4px;
  color: #9ca3af;
}

.tt-row {
  display: flex;
  align-items: center;
  gap: 6px;
  line-height: 1.6;
  white-space: nowrap;
}

.tt-key {
  flex-shrink: 0;
}

.tt-value {
  color: #fff;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.tt-label {
  color: #9ca3af;
}

.tt-total {
  margin-top: 2px;
  padding-top: 4px;
  padding-left: 18px;
  border-top: 1px solid #262626;
}

.caption {
  margin: 0;
  font-size: 12px;
  color: #8e8e8e;
}
</style>
